import {
  Trash2,
  Pin,
  PinOff,
  X,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { useConfirm } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { RichTextEditor } from '@/components/RichTextEditor';
import { webNoOutline } from '@/lib/web-style';
import { webTransition } from '@/lib/hover-style';
import type { NoteRow } from '@/db/types';

interface Props {
  visible: boolean;
  /** Null = compose a new note. Set = edit existing. */
  note: NoteRow | null;
  onClose: () => void;
  onSave: (input: { title: string | null; body: string; pinned: boolean }) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
}

/**
 * Notes editor — title row + WYSIWYG rich-text body via RichTextEditor.
 *
 * Why this rewrite (Wave 35):
 *   - The previous markdown-with-preview UI showed the user raw `**`
 *     and `#` characters. Pasting via Ctrl+V sometimes closed the
 *     modal because the backdrop Pressable received the synthetic
 *     event. Both fixed here:
 *     (a) RichTextEditor renders contentEditable on web (no syntax
 *         visible — formatting applies immediately).
 *     (b) The backdrop close path now ignores clicks whose target is
 *         a descendant of the inner card. The card itself also stops
 *         propagation on press events.
 */
export function NoteEditorModal({ visible, note, onClose, onSave, onDelete }: Props) {
  const confirm = useConfirm();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (visible) {
      setTitle(note?.title ?? '');
      setBody(note?.body ?? '');
      setPinned(note?.pinned === 1);
    }
  }, [visible, note]);

  const handleSave = async () => {
    const t = title.trim() || null;
    // The contenteditable body always carries SOME HTML when the user
    // has typed (even just `<br>`). Strip-and-check before we save to
    // catch the truly-empty case.
    const stripped = body.replace(/<[^>]+>/g, '').trim();
    if (!stripped) return;
    await onSave({ title: t, body, pinned });
    onClose();
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    // Notes are the one thing in the app that can't be regenerated —
    // free text lives nowhere else. Confirm like every other
    // destructive action does.
    //
    // Close the editor Modal FIRST: the confirm dialog is its own
    // root-level RN Modal, and iOS won't reliably present a second
    // modal while this one is up. The brief defer lets the dismissal
    // start before the confirm presents.
    onClose();
    await new Promise((r) => setTimeout(r, 300));
    const ok = await confirm({
      title: 'Delete this note?',
      message: 'There’s no undo — the note is gone for good.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await onDelete();
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}>
      <View style={styles.backdrop}>
        {/* Backdrop click = close. We use a separate full-bleed
            Pressable BEHIND the card so descendant events never reach
            this handler. This is what fixed Ctrl+V randomly closing
            the modal — RN-on-web's Pressable was capturing keyboard
            shortcut events that bubbled out of the textarea. */}
        <Pressable
          accessibilityLabel="Close editor"
          style={StyleSheet.absoluteFill}
          onPress={onClose}
        />
        <View style={styles.card} onStartShouldSetResponder={() => true}>
          {/* Header — title input + close + pin + delete. */}
          <View style={styles.header}>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Untitled note"
              placeholderTextColor={Palette.inkFaint}
              style={[styles.titleInput, webNoOutline]}
              maxLength={120}
            />
            <View style={styles.headerActions}>
              <Pressable
                accessibilityLabel={pinned ? 'Unpin note' : 'Pin note'}
                onPress={() => setPinned((p) => !p)}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.headerBtn,
                  pinned && styles.headerBtnActive,
                  pressed && { opacity: 0.7 },
                ]}>
                {pinned ? (
                  <Pin size={15} color={Palette.honeyDeep} strokeWidth={2.2} />
                ) : (
                  <PinOff size={15} color={Palette.inkSoft} strokeWidth={1.8} />
                )}
              </Pressable>
              {note && onDelete ? (
                <Pressable
                  accessibilityLabel="Delete note"
                  onPress={handleDelete}
                  hitSlop={8}
                  style={({ pressed }) => [
                    styles.headerBtn,
                    pressed && { opacity: 0.7 },
                  ]}>
                  <Trash2 size={15} color={Palette.clay} strokeWidth={1.8} />
                </Pressable>
              ) : null}
              <Pressable
                accessibilityLabel="Close"
                onPress={onClose}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.headerBtn,
                  pressed && { opacity: 0.7 },
                ]}>
                <X size={16} color={Palette.inkSoft} strokeWidth={1.8} />
              </Pressable>
            </View>
          </View>

          {/* The rich-text editor itself. Its toolbar is part of it so
              the modal stays a clean header + editor + footer stack. */}
          <View style={styles.editorContainer}>
            <RichTextEditor
              html={body}
              onChange={setBody}
              onSubmit={handleSave}
              placeholder="Start typing — bold, italic, lists, colors, all without seeing any markup."
            />
          </View>

          {/* Footer — Save button + character hint. */}
          <View style={styles.footer}>
            <Text style={styles.footerHint}>
              {body.replace(/<[^>]+>/g, '').trim().length === 0
                ? 'Format with the toolbar · pinned notes float to the top'
                : 'Ctrl+Enter to save'}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Save note"
              onPress={handleSave}
              disabled={body.replace(/<[^>]+>/g, '').trim().length === 0}
              style={({ pressed }) => [
                styles.saveBtn,
                webTransition,
                body.replace(/<[^>]+>/g, '').trim().length === 0 && { opacity: 0.4 },
                pressed && { transform: [{ scale: 0.96 }] },
              ]}>
              <Text style={styles.saveBtnText}>{note ? 'Save changes' : 'Save note'}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.three,
  },
  card: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    width: '100%',
    maxWidth: 720,
    maxHeight: '90%',
    overflow: 'hidden',
    flexDirection: 'column',
    minHeight: 420,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: Spacing.four,
    paddingRight: Spacing.three,
    paddingVertical: Spacing.three,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    gap: 8,
  },
  titleInput: {
    flex: 1,
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    paddingVertical: 4,
  },
  headerActions: { flexDirection: 'row', gap: 4 },
  headerBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBtnActive: { backgroundColor: Palette.honeySoft },
  editorContainer: { flex: 1, minHeight: 280 },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.three,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
    backgroundColor: Palette.sand,
    gap: Spacing.three,
  },
  footerHint: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
    flexShrink: 1,
  },
  saveBtn: {
    backgroundColor: Palette.forest,
    paddingHorizontal: Spacing.five,
    paddingVertical: 10,
    borderRadius: 999,
  },
  saveBtnText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    letterSpacing: 0.2,
  },
});
